import { ReactNode } from 'react';
import SideMenuNav from './SideMenuNav';
import { SideMenuGroup } from './menu';

/**
 * A rail of links beside the content — the part of a side-menu page that is the same
 * whichever brand is drawing it.
 *
 * Deliberately no chrome — a grid, a rail and a slot. This was built on top of
 * DiscountMags' `Full` when the Institutional and Account layouts were the only things
 * using it and both were DiscountMags'; sharing it with TopMags is what separated the
 * two. It stays separated now that both brands' `Full` draws the same box, for the
 * plainer reason that a rail is not chrome: what surrounds it — the white card TopMags
 * puts it on — is the brand's, and a layout supplies it. See `Layouts/Account` in each
 * brand; both are short.
 *
 * The breadcrumb is not here either, and for the older reason: it belongs to the page,
 * like the rest of the content. Drawing it above this grid stretched it across the rail
 * as well and left every page negotiating with its layout over its own trail.
 *
 * The one thing that cannot be worked out here is which entry is current, because a
 * server render has no url to read — it is the page's own path, and the props builder
 * puts it on `activePath`.
 */
export type SideMenuProps = {
  /** The mobile toggle's text. */
  label: string;
  /** The nav landmark's name, when it should differ from the toggle's. */
  ariaLabel?: string;
  groups: SideMenuGroup[];
  /** The menu entry to mark, as its path — `/reviews`. */
  activePath?: string;
  footer?: ReactNode;
  children?: ReactNode;
};

export default function SideMenu({
  children,
  label,
  ariaLabel,
  groups,
  activePath,
  footer,
}: SideMenuProps) {
  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[248px_1fr] lg:gap-9">
      <SideMenuNav
        label={label}
        ariaLabel={ariaLabel}
        groups={groups}
        activePath={activePath}
        footer={footer}
      />

      {/* The brand's Full already opened the page's <main>; a second one nested inside
          it is invalid. */}
      <div>{children}</div>
    </div>
  );
}
